import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Modal,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Spinner,
} from 'reactstrap';
import { Field, Form } from 'react-final-form';
import axios from 'axios';
import { useDispatch, flash, flashSuccess, flashError } from '@/shared/components/flash';
import { isFailedResponse } from '@/shared/helpers';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';
import FullPageLoader from '@/shared/components/FullPageLoader';

/**
 * Declines one pending RedemptionHold - shared by the program-manager queue
 * (Program/View/User/RedemptionHolds) and the Super Admin Redemption Risk
 * Review detail page (Approvals/Redemptions), which otherwise differed only
 * in what "declining" was described as and how the caller wanted to learn
 * of success. `description` carries that caller-specific text/markup;
 * `onSuccess` is the caller-specific refresh callback.
 */
const DeclineHoldModal = ({ organization, programId, holdId, description, isOpen, toggle, onSuccess, theme, rtl }) => {
    const dispatch = useDispatch();

    const onSubmit = (values) => {
        return axios.put(
            `/organization/${organization.id}/program/${programId}/redemption-hold/${holdId}/decline`,
            { review_notes: values.review_notes }
        )
            .then((response) => {
                if (isFailedResponse(response)) return;
                // The API answers 200 with the unchanged hold when it could not
                // be declined (an approval is in flight, or someone else already
                // acted on it), so check what it actually became.
                if (response.data.status !== 'DECLINED') {
                    flash('warning', dispatch, `This hold was not declined - its current status is ${response.data.status}.`);
                } else {
                    flashSuccess(dispatch, 'Redemption declined.');
                }
                toggle();
                if (onSuccess) onSuccess();
            })
            .catch(error => {
                flashError(dispatch, error.response?.data);
            });
    };

    return (
        <Modal className={`modal-action modal-md ${theme?.className || ''} ${rtl?.direction || 'ltr'}-support`} isOpen={isOpen} toggle={toggle}>
            <Card className="w-100">
                <CardHeader tag="h3">
                    Decline Redemption
                    <Button className="btn float-end" close onClick={toggle} />
                </CardHeader>
                <CardBody>
                    <CardTitle className="text-left">
                        {description}
                    </CardTitle>
                    <Form
                        onSubmit={onSubmit}
                    >
                        {({ handleSubmit, submitting }) => (
                            <form className="form" onSubmit={handleSubmit}>
                                <FullPageLoader show={submitting} text="Declining redemption..." />
                                <Field name="review_notes">
                                    {({ input }) => (
                                        <div className="form__form-group">
                                            <span className="form__form-group-label">Notes (optional):</span>
                                            <div className="form__form-group-field">
                                                <div className="form__form-group-row">
                                                    <input type="text" {...input} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Field>
                                <div className="row d-flex justify-content-end">
                                    <Button className="btn-sm mr-2" color="primary" type="submit" disabled={submitting}>
                                        {submitting ? <>Processing <Spinner size="sm" className="ms-1 align-middle" /></> : 'Decline Redemption'}
                                    </Button>
                                    <Button className="btn-sm" color="outline-primary" onClick={toggle} disabled={submitting}>Cancel</Button>
                                </div>
                            </form>
                        )}
                    </Form>
                </CardBody>
            </Card>
        </Modal>
    );
};

DeclineHoldModal.propTypes = {
    theme: ThemeProps.isRequired,
    rtl: RTLProps.isRequired,
};

export default withRouter(connect((state) => ({
    theme: state.theme,
    rtl: state.rtl,
    organization: state.organization,
}))(DeclineHoldModal));
